import { Dictionary } from 'lodash'
import Chance from '../utils/Chance'
import Event, { IEventDispatcher } from '../utils/Event'
import Point, { pt } from '../utils/Point'
import Card, { CardType } from './Card'
import Exit from './cards/Exit'
import Deck from './Deck'
import Game from './Game'
import Hero from './Hero'

interface GameBoardEvents {
  cardDisplayed: Event<GameBoard, Card>
  cardDrawn: Event<GameBoard, Card>
  cardDiscarded: Event<GameBoard, Card>
  cardPicked: Event<GameBoard, Card>
}

export default class GameBoard implements IEventDispatcher<GameBoard, GameBoardEvents> {
  cards = new Map<Point, Card>()
  decks = new Map<Point, Deck>()

  slideValues: Dictionary<number> = {}

  hero: Hero

  events: GameBoardEvents = Event.list(this, 'cardDisplayed', 'cardDrawn', 'cardDiscarded', 'cardPicked')

  private static error: string

  constructor(readonly width: number, readonly height: number, public game: Game, public cardGenerator: () => Card) {
    this.hero = game.hero
    this.hero.setBoard(this)

    this.initEmptyDecks()
    this.initExit()
    this.initHeroCard()
    this.drawCards()
  }

  forEachPoint(callback: (pt: Point) => void) {
    for (let i = 0; i < this.width; i++) {
      for (let j = 0; j < this.height; j++) {
        callback(pt(i, j))
      }
    }
  }

  points() {
    const pts: Point[] = []
    this.forEachPoint(pt => pts.push(pt))
    return pts
  }

  initHeroCard() {
    const p = Chance.in(...this.points().filter(pt => !this.cardAt(pt)))
    this.hero.card.moveTo(p, this, true)
  }

  initExit() {
    const p = Chance.in(...this.points().filter(pt => !this.cardAt(pt)))
    new Exit().moveTo(p, this, true)

    for (let i = 0; i < 8; i++) {
      this.deckAt(p)!.add(new Exit())
    }
  }

  initEmptyDecks() {
    this.forEachPoint(pt => {
      this.decks.set(pt, new Deck(this, pt))
    })
  }

  minDeckSize() {
    return Array.from(this.decks)
      .map(([, deck]) => deck.size)
      .reduce((x, y) => Math.min(x, y))
  }

  drawCards() {
    this.forEachPoint(pt => {
      if (this.cardAt(pt.x, pt.y)) return
      const card = this.cardGenerator().moveTo(pt, this, true)
      this.events.cardDisplayed.dispatch(card)
    })
  }

  cardAt(pt: Point): Card | undefined
  cardAt(x: number, y: number): Card | undefined
  cardAt(ptOrX: number | Point, y?: number): Card | undefined {
    return this.cards.get(ptOrX instanceof Point ? ptOrX : pt(ptOrX, y!))
  }

  deckAt(pt: Point): Deck | undefined
  deckAt(x: number, y: number): Deck | undefined
  deckAt(ptOrX: number | Point, y?: number): Deck | undefined {
    return this.decks.get(ptOrX instanceof Point ? ptOrX : pt(ptOrX, y!))
  }

  rowAt(location: Point) {
    const cards: Card[] = []
    for (let i = 0; i < this.width; i++) {
      cards.push(this.cardAt(i, location.y)!)
    }
    return cards
  }

  colAt(location: Point) {
    const cards: Card[] = []
    for (let i = 0; i < this.height; i++) {
      cards.push(this.cardAt(location.x, i)!)
    }
    return cards
  }

  removeCardAt(location: Point) {
    this.cards.delete(location)
  }

  toMatrix() {
    const m: [Card, Deck][][] = []
    this.forEachPoint(pt => {
      m[pt.x] = m[pt.x] || []
      m[pt.x][pt.y] = [this.cardAt(pt.x, pt.y)!, this.deckAt(pt.x, pt.y)!]
    })
    return m
  }

  fromMatrix(matrix: [Card, Deck][][]) {
    matrix.forEach((arr, i) => arr.forEach(([card, deck], j) => {
      const p = pt(i, j)
      card.board = deck.board = this
      card.location = deck.location = p
      this.cards.set(p, card)
      this.decks.set(p, deck)
    }))
  }

  rotate() {
    const m = this.toMatrix().slice()

    for (let i = 0; i < m.length; i += 1)
      for (let j = i + 1; j < m.length; j += 1)
        [m[j][i], m[i][j]] = [m[i][j], m[j][i]]

    for (let i = 0; i < m.length; i += 1)
      for (let j = 0; j < m.length / 2; j += 1)
        [m[i][m.length - j - 1], m[i][j]] = [m[i][j], m[i][m.length - j - 1]]

    this.fromMatrix(m)
  }

  static setError(error: string) {
    GameBoard.error = error
  }

  static clearError() {
    delete GameBoard.error
  }

  static getError() {
    return GameBoard.error
  }
}
